import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import { Shield, ShieldOff, AlertTriangle } from 'lucide-react';

const BlockButton = ({ 
  targetUser, 
  onBlockChange, 
  className = '', 
  size = 'md',
  showConfirmModal = true 
}) => {
  const { user } = useAuth();
  const [isBlocking, setIsBlocking] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false); // Estado local del bloqueo

  // No mostrar el botón si es el mismo usuario
  if (!user || !targetUser || user.id === targetUser.id) {
    return null;
  }

  const handleBlockClick = () => {
    if (showConfirmModal) {
      setShowBlockModal(true);
    } else {
      handleBlock();
    }
  };

  const handleUnblockClick = () => {
    if (showConfirmModal) {
      setShowUnblockModal(true);
    } else {
      handleUnblock();
    }
  };

  const handleBlock = async () => {
    setIsBlocking(true);
    try {
      await apiService.blockUser(targetUser.id);
      setIsBlocked(true);
      setShowBlockModal(false);
      
      // Notificar al componente padre del cambio
      if (onBlockChange) {
        onBlockChange(targetUser.id, true);
      }
      
      
      // Mostrar mensaje de éxito
    } catch (error) {
      alert('Error al bloquear usuario: ' + error.message);
    } finally {
      setIsBlocking(false);
    }
  };

  const handleUnblock = async () => {
    setIsBlocking(true);
    try {
      await apiService.unblockUser(targetUser.id);
      setIsBlocked(false);
      setShowUnblockModal(false);
      
      // Notificar al componente padre del cambio
      if (onBlockChange) {
        onBlockChange(targetUser.id, false);
      }
      
      
      // Mostrar mensaje de éxito
    } catch (error) {
      alert('Error al desbloquear usuario: ' + error.message);
    } finally {
      setIsBlocking(false);
    }
  };

  const getButtonClasses = () => {
    const baseClasses = `flex items-center justify-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg`;
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm rounded-md',
      md: 'px-4 py-2 text-sm rounded-lg',
      lg: 'px-6 py-3 text-base rounded-lg'
    };
    
    const colorClasses = isBlocked 
      ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-105 active:scale-95'
      : 'bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95';
    
    const disabledClasses = isBlocking 
      ? 'opacity-75 cursor-not-allowed' 
      : '';
    
    return `${baseClasses} ${sizeClasses[size]} ${colorClasses} ${disabledClasses} ${className}`;
  };

  const getButtonContent = () => {
    if (isBlocking) {
      return (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span>{isBlocked ? 'Desbloqueando...' : 'Bloqueando...'}</span>
        </>
      );
    }
    
    return (
      <>
        {isBlocked ? (
          <>
            <ShieldOff className="h-4 w-4" />
            <span>Desbloquear</span>
          </>
        ) : (
          <>
            <Shield className="h-4 w-4" />
            <span>Bloquear</span>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <button
        onClick={isBlocked ? handleUnblockClick : handleBlockClick}
        disabled={isBlocking}
        className={getButtonClasses()}
        title={isBlocked ? 'Desbloquear usuario' : 'Bloquear usuario'}
      >
        {getButtonContent()}
      </button>

      {/* Modal de confirmación para bloquear */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Bloquear usuario</h3>
              </div>
              <button
                onClick={() => setShowBlockModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-all duration-200"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres bloquear a <strong>{targetUser.name}</strong>?
                <br />
                <span className="text-sm text-gray-500">
                  No podrás ver sus mensajes ni aparecerá en tus búsquedas ni podrás DESBLOQUEARLO.
                </span>
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBlock}
                  disabled={isBlocking}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isBlocking 
                      ? 'bg-red-400 text-white cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isBlocking ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Bloqueando...</span>
                    </>
                  ) : (
                    <span>Sí, bloquear</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para desbloquear */}
      {showUnblockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <ShieldOff className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Desbloquear usuario</h3>
              </div>
              <button
                onClick={() => setShowUnblockModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-all duration-200"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres desbloquear a <strong>{targetUser.name}</strong>?
                <br />
                <span className="text-sm text-gray-500">
                  Podrás ver sus mensajes y aparecerá en tus búsquedas nuevamente.
                </span>
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUnblockModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUnblock}
                  disabled={isBlocking}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isBlocking 
                      ? 'bg-green-400 text-white cursor-not-allowed' 
                      : 'bg-green-600 text-white hover:bg-green-700 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isBlocking ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Desbloqueando...</span>
                    </>
                  ) : (
                    <span>Sí, desbloquear</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlockButton;
